"use client";
import React, { useEffect, useRef, useState } from "react";
import { Img } from "./Img";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import {
  AppRoutesEnum,
  START_TRIAL_ANIMATE_IN_TIMEOUT,
  SUBSCRIPTION_STATUS,
} from "@/constants/constants";
import { isInternationalUser } from "@/utils/location.helpers";
import { Text } from "./Text";
import {
  getPaywallLink,
  shouldRedirectToSubscriptionFlow,
} from "@/utils/common.helpers";
import Link from "next/link";

const StartTrialBanner = ({
  subscriptionStatus,
  lang,
  dialect,
}: {
  subscriptionStatus: number;
  lang: string;
  dialect: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useClientTranslations();
  const isTrialEligible =
    subscriptionStatus === SUBSCRIPTION_STATUS.NOT_SUBSCRIBED &&
    !shouldRedirectToSubscriptionFlow() &&
    !isInternationalUser();

  // Animate in after mount
  useEffect(() => {
    const timer = setTimeout(
      () => setIsVisible(true),
      START_TRIAL_ANIMATE_IN_TIMEOUT,
    );
    return () => clearTimeout(timer);
  }, []);

  const paywallLink = getPaywallLink(
    subscriptionStatus === SUBSCRIPTION_STATUS.NOT_SUBSCRIBED,
    shouldRedirectToSubscriptionFlow(),
    lang,
    dialect,
  );

  return (
    <div
      className={`align-center mx-4 mb-4 flex transform items-center justify-between gap-0 rounded-lg bg-stg-floating-banner-background p-4 backdrop-blur-sm transition-all duration-500 ease-in-out ${
        isVisible
          ? "translate-y-0"
          : "pointer-events-none -translate-y-6 opacity-0"
      }`}
    >
      <Img
        src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${"/static/stage_logo_stack.png"}`}
        alt={"Stage Logo"}
        width={40}
        height={40}
        className="rounded-lg bg-stg-grey-3 p-2"
      />
      <div className="ms-2 flex min-w-0 flex-1 items-center justify-between whitespace-nowrap">
        <p className="min-w-0 flex-1 whitespace-normal break-words pr-3 text-sm font-bold leading-[1.35] !text-stg-grey-1">
          {isTrialEligible
            ? t("start_trial_banner.heading.trialEligible")
            : t("start_trial_banner.heading.notTrialEligible")}
        </p>
        <Link
          href={
            !!dialect
              ? paywallLink
              : `${AppRoutesEnum.LOGIN}?redirectionAfterLoginTo=${paywallLink}`
          }
          className="rounded-lg bg-stg-green p-2"
        >
          <Text size={2} weight="medium">
            {t("start_trial_banner.cta")}
          </Text>
        </Link>
      </div>
    </div>
  );
};

export default StartTrialBanner;
